import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import Heading from 'components/Luxkit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import YouMayAlsoLikeOffersCarousel from 'components/Recommendations/YouMayAlsoLike/Shared/YouMayAlsoLikeOffersCarousel'
import TrackingContext from 'contexts/trackingContext'
import { calcDistanceBetweenOffers } from 'lib/offer/offerUtils'
import { DistanceUnit } from 'components/OfferList/OfferListTiles/Support/searchDistanceLabel'
import { take } from 'lib/array/arrayUtils'
import useOffers from 'hooks/Offers/useOffers'
import Group from 'components/utils/Group'
import GeoContext from 'contexts/geoContext'
import { FRONT_PAGE_OFFERS_TYPES } from 'constants/offerList'
import useOfferList from 'hooks/Offers/useOfferList'
import * as Analytics from 'analytics/analytics'
import { clickRecommendationOnOfferPage, viewRecommendationOnOfferPage } from 'analytics/optimizely/events'
import { useInView } from 'react-intersection-observer'
import { OptimizelyServerSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'

const Root = styled.div`
  position: relative;
  background-color: ${props => props.theme.palette.neutral.eight};
  padding-bottom: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.M} ${props => props.theme.borderRadius.M} 0 0;
  z-index: ${zIndex.stickyFooter};
`

const HeaderGroup = styled(Group)`
  padding-top: ${rem(16)};
  padding-bottom: ${rem(16)};
`

export const LERE_CUSTOMERS_VIEWED_AND_BOOKED_NUM = 3

export interface Props {
  distanceUnit?: DistanceUnit;
  filters?: App.OfferListFilters;
  hasEnoughRecommendationsToDisplay: boolean;
  lereVersion: string | undefined;
  loadingError?: any;
  loadingState: App.ApiResponseState;
  seedOffer?: App.Offer | App.BedbankOffer;
  offerIds: Array<string>;
}

function CustomerViewedAndBooked({
  distanceUnit,
  filters,
  loadingState,
  seedOffer,
  offerIds,
}: Props) {
  const trackingContextExternal = useContext(TrackingContext)

  // append the top-3 front offers in the end in case no enough recommendations
  const { currentRegionCode } = useContext(GeoContext)
  const offerListFilters = useMemo(() => {
    return {
      offerTypes: FRONT_PAGE_OFFERS_TYPES(currentRegionCode),
      frontPageVisibleOnly: true,
      excludeConnectionTours: true,
    }
  }, [currentRegionCode])
  // get the front page offers and filter out the offer ids from LERE and source offers
  const excludedOfferIds = seedOffer ? [...offerIds, seedOffer.id] : offerIds
  const offerList = useOfferList(offerListFilters, { excludedIds: excludedOfferIds })
  const frontOfferIds = take(offerList.offerIds, 3)

  const candidatesOfferIds = [...offerIds, ...frontOfferIds]

  const [offers, offerDetailsLoading] = useOffers<App.Offer | App.BedbankOffer>(take(candidatesOfferIds, 3))

  const offerDistanceFrom = seedOffer?.property?.name

  const offerDistances = useMemo(() => {
    const offerIdDistances: Record<string, number | null> = {}
    if (!seedOffer) {
      return offerIdDistances
    }
    offers.forEach((offer) => {
      offerIdDistances[offer.id] = calcDistanceBetweenOffers(offer, seedOffer)
    })
    return offerIdDistances
  }, [offers, seedOffer])

  const readyToRender = ['done', 'error'].includes(loadingState) && !offerDetailsLoading

  // the view and the click events for optimisely AB tests
  const onTileClick = useCallback(() => {
    Analytics.track('click-recommendation-on-offer-page', clickRecommendationOnOfferPage())
  }, [])

  const { ref, inView } = useInView({ triggerOnce: true })

  // call this hooks to activate the experiement for users (the variant fetched on the ssr already)
  useOptimizelyExperiment(OptimizelyServerSideExperiments.lereOfferPageEmbeddedCarousel, inView)

  useEffect(() => {
    if (inView && readyToRender) {
      Analytics.track('view-recommendation-on-offer-page', viewRecommendationOnOfferPage())
    }
  }, [inView, readyToRender])

  return (
    <Root
      className="customer-viewed-and-booked"
      role="button"
      data-testid="lere-customer-viewed-and-booked"
    >
      <LayoutContainer ref={ref}>
        <HeaderGroup direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
          <Heading variant="heading5">Customers who viewed this offer end up booking these:</Heading>
        </HeaderGroup>
        <YouMayAlsoLikeOffersCarousel
            offers={offers}
            offerDistances={offerDistances}
            offerDistanceFrom={offerDistanceFrom}
            offerDistanceUnit={distanceUnit}
            tracking={trackingContextExternal}
            filters={filters}
            pageSize={LERE_CUSTOMERS_VIEWED_AND_BOOKED_NUM}
            readyToRender={readyToRender}
            onTileClick={onTileClick}
        />
      </LayoutContainer>
    </Root>
  )
}

export default CustomerViewedAndBooked
